import GroupChevron from '../GroupChevron';
import CellCheckbox from '../CellCheckbox';
import CellFavorite from '../CellFavorite';
import Children from './Children';
import { showDataGridTooltip, hideDataGridTooltip } from '../../../utils/createTooltipPopper';
import {
    getDataGridScrollViewport,
    getDataGridTooltipElement,
    getTooltipReferenceElement,
} from '../../../utils/getDataGridTooltipElement';
import {
    attachActionsWrapperListeners,
    attachRowActionsListeners,
    getBodyViewportScrollState,
} from '../../../utils/actionsOverlay';
import { queryGridScrollViewport, resolveDataGridRoot } from '../../../utils/agGridDomSelectors';
import { isRowDragEnabled } from '../../../utils/agGridColumn';
import {
    getCellOnClick,
    getChildrenOnClick,
    getCellRendererParam,
    invokeCellClickHandler,
} from '../../../utils/cellClick';
import { isDataGridCellNestedInteraction } from '../../../utils/isDataGridCellNestedInteraction';

let hoveredRowId;

function addHoverClass(el) {
    const rows = document.querySelectorAll('.ag-row--action-hover');
    for (let row of rows) {
        row.classList?.remove('ag-row--action-hover');
    }
    const rowsHovered = el.querySelectorAll(`[row-id='${hoveredRowId}']`);
    for (let row of rowsHovered) {
        row.classList?.add('ag-row--action-hover');
    }
}

export default function BaseCell(cell, params) {
    if (!params) return null;
    const baseCell = document.createElement('div');
    baseCell.classList.add('w5-cell');
    if (params?.node?.id) baseCell.setAttribute('data-rowid', params.node.id);
    const data = params.data;

    if (params.customStyles) {
        Object.keys(params.customStyles).forEach((current) => {
            baseCell.style[current] = params.customStyles[current];
        });
    }

    // Important the optional chaining because if it's a children row, it has no colDef
    // and it will break
    if (isRowDragEnabled(params)) baseCell.classList.add('w5-cell-drag');

    if (
        params.context?.useDragCustom &&
        params.column &&
        params.api &&
        ((params.context?.canDrag && params.context?.canDrag(data)) || !params.context?.canDrag)
    ) {
        const colDef = params.column.colDef;
        const columns = params.api.getColumns();
        if (colDef.colId === columns[0].colId) {
            const customDragger = document.createElement('div');
            customDragger.classList.add('w5-cell__custom-dragger');
            const dragger = `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M0 18c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm0-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm1 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm0-6c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2z" fill="var(--actionMinor-400)" />
                            </svg>`;
            customDragger.innerHTML = dragger;
            baseCell.appendChild(customDragger);
        }
    }
    addCellClasses(baseCell, params);
    addChildrenCellClasses(baseCell, params);

    if (params?.checkbox?.onClick) {
        const checkbox = CellCheckbox(params);
        checkbox && baseCell.appendChild(checkbox);
    }

    if (params?.favorite?.onClick) {
        const favorite = CellFavorite(params);
        favorite && baseCell.appendChild(favorite);
    }

    if (params?.withGroup) {
        const chevron = GroupChevron(params);
        chevron && baseCell.appendChild(chevron);
    }

    let finalCell;
    if (params?.data?.isFullWidth) finalCell = cell;
    else if (params?.childrenParams && params?.data?.isChildren) {
        finalCell = Children(params);
        if (params?.withGroup) {
            finalCell.classList.add('w5-cell__children-first-cell');
        } else {
            finalCell.classList.add('w5-cell__children');
        }
    } else finalCell = cell;

    addCellOnClick(baseCell, params);
    addChildrenCellOnClick(baseCell, params);
    addNonClickCellHoverLink(baseCell, params);

    const getUrl = getCellRendererParam(params, 'getUrl');
    if (getUrl) {
        let linkWrapper;
        if (data?.isChildren) {
            linkWrapper = addChildrenLinkWrapper(finalCell, params, data);
            if (linkWrapper) finalCell = linkWrapper;
        } else {
            linkWrapper = addLinkWrapper(finalCell, params, data);
            if (linkWrapper) finalCell = linkWrapper;
        }
    }

    if (finalCell) baseCell.appendChild(finalCell);

    function isValidValue(value) {
        if (value === undefined || value === null || value === '') return false;
        return true;
    }

    if (
        params.useTooltip &&
        (typeof params.tooltipValue === 'function' ||
            (params.tooltipKey && isValidValue(data?.[params.tooltipKey])) ||
            (!params.tooltipKey && isValidValue(params.value)))
    ) {
        const tooltip = getDataGridTooltipElement(params?.context?.tableId);
        let scrollViewport = null;

        if (!tooltip) return baseCell;

        const updateTooltipPosition = () => {
            const tooltipReference = getTooltipReferenceElement(baseCell, params.eGridCell);
            const hasCompactReference =
                tooltipReference !== baseCell && tooltipReference !== params.eGridCell;

            let placement = 'top-start';
            if (hasCompactReference) placement = 'top';
            if (params.align === 'right') placement = 'top-end';
            if (params.align === 'center') placement = 'top';

            showDataGridTooltip(tooltipReference, tooltip, { placement });
        };

        const onScroll = () => {
            if (tooltip.style.display === 'block') {
                updateTooltipPosition();
            }
        };

        baseCell.addEventListener('mouseover', () => {
            tooltip.firstElementChild.innerText =
                (params?.tooltipValue && params?.tooltipValue(params.value)) ||
                data[params.tooltipKey] ||
                params.value;

            updateTooltipPosition();

            scrollViewport = getDataGridScrollViewport(params.eGridCell);
            scrollViewport?.addEventListener('scroll', onScroll, { passive: true });
        });

        baseCell.addEventListener('mouseout', () => {
            hideDataGridTooltip(tooltip);
            scrollViewport?.removeEventListener('scroll', onScroll);
            scrollViewport = null;
        });
    }

    if (params?.context?.hasActions && !params?.data?.isFullWidth) {
        controlActionsDisplay(baseCell, params);
    }

    return baseCell;
}

function addCellClasses(baseCell, params) {
    if (!params?.data?.isChildren || (params?.data?.isChildren && !params?.childrenParams)) {
        if (params?.bold) baseCell.classList.add('w5-cell--bold');
        if (params?.link) baseCell.classList.add('w5-cell--link');
        if (params?.align) baseCell.classList.add(`w5-cell--align-${params.align}`);
    }
}

function addChildrenCellClasses(baseCell, params) {
    if (params?.data?.isChildren) {
        if (params?.childrenParams?.bold) baseCell.classList.add('w5-cell--bold');
        if (params?.childrenParams?.link) baseCell.classList.add('w5-cell--link');
        if (params?.childrenParams?.align)
            baseCell.classList.add(`w5-cell--align-${params.childrenParams.align}`);
    }
}

function addCellOnClick(baseCell, params) {
    const onClick = getCellOnClick(params);
    const getUrl = getCellRendererParam(params, 'getUrl');
    const forceUrl = getCellRendererParam(params, 'forceUrl');
    if ((!getUrl || (getUrl && forceUrl)) && baseCell && onClick && !params?.data?.isChildren) {
        baseCell.classList.add('w5-cell--click');
        if (getCellRendererParam(params, 'hoverLink')) {
            baseCell.classList.add('w5-cell-click--with-hover');
        }
        baseCell.addEventListener(
            'click',
            (event) => {
                if (isDataGridCellNestedInteraction(event.target)) return;

                event.stopPropagation();
                if (forceUrl) event.preventDefault();
                invokeCellClickHandler(params);
            },
            true,
        );
    }
}

function addChildrenCellOnClick(baseCell, params) {
    const onClick = getChildrenOnClick(params);
    if (
        !getCellRendererParam(params, 'getUrl') &&
        baseCell &&
        onClick &&
        params?.data?.isChildren
    ) {
        baseCell.classList.add('w5-cell--click');
        if (getCellRendererParam(params, 'hoverLink')) {
            baseCell.classList.add('w5-cell-click--with-hover');
        }
        baseCell.addEventListener(
            'click',
            (event) => {
                if (isDataGridCellNestedInteraction(event.target)) return;

                event.stopPropagation();
                invokeCellClickHandler(params);
            },
            true,
        );
    }
}

function addNonClickCellHoverLink(baseCell, params) {
    if (
        getCellRendererParam(params, 'hoverLink') &&
        baseCell &&
        !getCellOnClick(params) &&
        !getChildrenOnClick(params)
    ) {
        baseCell.classList.add('w5-cell--non-click-with-hover');
    }
}

function addLinkWrapper(finalCell, params, data) {
    const onClick = getCellOnClick(params);
    const forceUrl = getCellRendererParam(params, 'forceUrl');
    const getUrl = getCellRendererParam(params, 'getUrl');
    if ((!onClick || (onClick && forceUrl)) && finalCell && getUrl && data) {
        const linkWrapper = document.createElement('a');
        linkWrapper.classList.add('w5-cell-link-wrapper');
        if (getCellRendererParam(params, 'hoverLink')) {
            linkWrapper.classList.add('w5-cell-link-wrapper--with-hover');
        }
        const url = getUrl(data, params.value);
        if (!url) return null;
        linkWrapper.href = url;
        linkWrapper.appendChild(finalCell);
        return linkWrapper;
    }
}

function addChildrenLinkWrapper(finalCell, params, data) {
    const onClick = getCellOnClick(params);
    const forceUrl = getCellRendererParam(params, 'forceUrl');
    const getUrl = getCellRendererParam(params, 'getUrl');
    if ((!onClick || (onClick && forceUrl)) && finalCell && getUrl && data) {
        const linkWrapper = document.createElement('a');
        linkWrapper.classList.add('w5-cell-link-wrapper');
        if (getCellRendererParam(params, 'hoverLink')) {
            linkWrapper.classList.add('w5-cell-link-wrapper--with-hover');
        }
        const url = getUrl(data, params.value);
        if (!url) return null;
        linkWrapper.href = url;
        linkWrapper.appendChild(finalCell);
        return linkWrapper;
    }
}

function controlActionsDisplay(baseCell, params) {
    const node = params.node;
    const tableId = params?.context?.tableId || '';
    const elementId = tableId ? `w5-actions-component-${tableId}` : 'w5-actions-component';

    function getBodyViewPort() {
        const tableRoot =
            resolveDataGridRoot({
                tableId: params?.context?.tableId,
                eGridCell: params?.eGridCell,
            }) ||
            params?.eGridCell?.closest('.ag-root-wrapper') ||
            null;
        return queryGridScrollViewport(tableRoot);
    }

    const actionsWrapper = document.getElementById(elementId);
    const dataGridRoot = resolveDataGridRoot({
        tableId: params?.context?.tableId,
        eGridCell: params?.eGridCell,
    });
    const rowElement = params?.eGridCell?.closest('.ag-row');

    attachRowActionsListeners({
        rowElement,
        actionsWrapper,
        dataGridRoot,
        context: params.context,
        node,
        data: params.data,
        getBodyViewPort,
        onRowHover: (rowId) => {
            hoveredRowId = rowId;
        },
    });

    attachActionsWrapperListeners(actionsWrapper, getBodyViewPort, params.context, () =>
        addHoverClass(getBodyViewPort()),
    );
}
